<script lang="ts" setup>
import axios from 'axios'
import { useI18n } from 'vue-i18n'
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import { DefaultRemark, DefaultRemarksType } from '@/types/cargo-loading'
import { GetDataParameters } from '@/types/table'
import { usePaginatedTable } from '@/hooks/table/use-paginated-table'
import { PaginatedResponse, uuid } from '@/types/general'
import { useAuthStore } from '@/stores/auth-store'
import { useMittListener } from '@/hooks/use-mitt-listener'
import { differenceFromMinutes } from '@/utils/dates'
import { nameAndShortCodeString } from '@/utils/cargo-loading'

import ManageDefaultRemarkModal from '@/components/cargo-loading/ManageDefaultRemarkModal.vue'
import MyTable from '@/components/table/MyTable.vue'
import MyTableColumn from '@/components/table/MyTableColumn.vue'
import ActionRowItem from '@/components/table/ActionRowItem.vue'

const { t } = useI18n()
const authStore = useAuthStore()
const route = useRoute()

const weekdayNames: Record<number, string> = {
    0: 'monday',
    1: 'tuesday',
    2: 'wednesday',
    3: 'thursday',
    4: 'friday',
    5: 'saturday',
    6: 'sunday',
}

interface FetchDefaultRemarksParams extends Record<string, unknown>, GetDataParameters {
    filter: {
        'loading-list'?: boolean
        routes?: boolean
    }
}

const { data, paginationData, params, loading, error, refetch } = usePaginatedTable<
    DefaultRemark,
    FetchDefaultRemarksParams
>(
    async (params, abortController) => {
        const response = await axios.get<PaginatedResponse<DefaultRemark>>(
            window.route('cl.company.default-remarks.index', {
                company: authStore.companyId,
            }),
            { params: params, signal: abortController.signal },
        )
        return response.data
    },
    {
        filter: {
            'loading-list': true,
            routes: undefined,
        },
    },
)

const manageDefaultRemarkModalOpen = ref(false)
const selectedRemark = ref<uuid | null>()

const isRoutes = computed(() => route.query['tab'] === 'routes')

function updateRemark(id: uuid) {
    selectedRemark.value = id
    manageDefaultRemarkModalOpen.value = true
}

function closeRemark() {
    selectedRemark.value = null
    manageDefaultRemarkModalOpen.value = false
    return
}

watch(
    () => route.query['tab'],
    () => {
        params.value.filter['loading-list'] = isRoutes.value ? undefined : true
        params.value.filter['routes'] = !isRoutes.value ? undefined : true
        refetch()
    },
)

useMittListener('fetchDefaultRemarks', () => refetch())
</script>

<template>
    <MyTable
        table-id="default-remarks"
        :rows="data"
        :pagination-data="paginationData"
        :loading="loading"
        :error="error"
        :get-data="refetch"
        :entity-name="t('remarks')"
    >
        <template #actionRow="{ row }">
            <ActionRowItem @click="updateRemark(row.id)">
                <span>
                    <mdi:pencil />
                </span>
            </ActionRowItem>
        </template>

        <MyTableColumn property="provider.name" :name="t('provider')" />
        <template #[`provider.name`]="{ row }">
            {{ nameAndShortCodeString(row.provider.name, row.providerAccountNumber) }}
        </template>
        <MyTableColumn property="providerNumber" :name="t('providerNumber')" />
        <MyTableColumn v-if="isRoutes" property="carrier.name" :name="t('carrier')" />
        <template #[`carrier.name`]="{ row }">
            <span v-if="row.carrier">
                {{ nameAndShortCodeString(row.carrier.name, row.carrierAccountNumber) }}
            </span>
        </template>
        <MyTableColumn v-if="!isRoutes" property="weekday" :name="t('weekday')" />
        <template #weekday="{ row }">
            {{ t(weekdayNames[row.weekday]) }}
        </template>
        <MyTableColumn v-if="!isRoutes" property="type" :name="t('type')" />
        <template #type="{ row }">
            {{ t(row.type === DefaultRemarksType.Plane ? 'plane' : 'truck') }}
        </template>
        <MyTableColumn v-if="!isRoutes" property="departureTime" :name="t('departureTime')" />
        <template #departureTime="{ row }">
            {{ differenceFromMinutes(row.departureTime) }}
        </template>
        <MyTableColumn v-if="!isRoutes" property="loadingTime" :name="t('loadingTime')" />
        <template #loadingTime="{ row }">
            {{ differenceFromMinutes(row.loadingTime) }}
        </template>
        <MyTableColumn property="remark" :name="t('remark')" />

        <ManageDefaultRemarkModal
            v-if="selectedRemark && route.query.tab"
            :id="selectedRemark"
            :for-route="route.query.tab.toString() === 'routes'"
            :model-value="manageDefaultRemarkModalOpen"
            @close="closeRemark()"
        />
    </MyTable>
</template>
